import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from "react";
import type { Room } from "colyseus.js";
import { getStateCallbacks } from "colyseus.js";
import type { ChatChannel, ChatErrorEvent, ChatErrorReason, GlobalChatMessage, GlobalState } from "@game/types";

const CHAT_ERR_TEXT: Record<ChatErrorReason, string> = {
  rate_limited: "You're sending messages too fast.",
  muted: "You are muted.",
  target_offline: "That player is offline.",
  whisper_target_required: "Whisper needs a target.",
  not_in_party: "You're not in a party.",
  party_missing: "Party no longer exists.",
  profanity: "Message blocked by auto-mod.",
  link_blocked: "Links to that domain are not allowed.",
  flood: "Looks like a repeat — slow down.",
  spam: "Detected as spam.",
  too_long: "Message too long (max 500 characters).",
  empty: "Message is empty.",
  blocked: "Message blocked.",
};

interface Props {
  /** Connected `global` room. Null = chat is disabled. */
  globalRoom: Room<GlobalState> | null;
  /** Sticky muted-until — drives chat input disabled state. */
  mutedUntil?: string | null;
}

const wrapStyle: CSSProperties = {
  position: "fixed",
  left: 8,
  bottom: 116,
  width: 360,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 12,
  color: "#e6e8ee",
  display: "flex",
  flexDirection: "column",
  zIndex: 980,
};

const channelColor = (channel: ChatChannel | string): string => {
  switch (channel) {
    case "system":
      return "#facc15";
    case "whisper":
      return "#c084fc";
    case "party":
      return "#86efac";
    case "global":
    case "say":
    default:
      return "#7dd3fc";
  }
};

export function GameChat({ globalRoom, mutedUntil }: Props) {
  const [messages, setMessages] = useState<Array<{ id: string } & GlobalChatMessage>>([]);
  const [draft, setDraft] = useState("");
  const [channel, setChannel] = useState<ChatChannel>("global");
  const [whisperTo, setWhisperTo] = useState<string>("");
  const [error, setError] = useState<{ text: string; until?: number } | null>(null);
  const [muteUntilTs, setMuteUntilTs] = useState<number | null>(
    mutedUntil ? new Date(mutedUntil).getTime() : null,
  );
  const [, forceTick] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);

  // Subscribe to chat
  useEffect(() => {
    if (!globalRoom) return;
    const $ = getStateCallbacks(globalRoom);
    const unsubs: Array<() => void> = [];

    const cb = $(globalRoom.state).recentChat.onAdd((m) => {
      setMessages((prev) => [...prev, { id: `${m.ts}-${m.fromName}`, ...m }].slice(-200));
    });
    unsubs.push(() => cb());

    const onWhisper = globalRoom.onMessage("whisper", (m: { fromCharacterId: string; fromName: string; body: string; ts: number }) => {
      setMessages((prev) => [
        ...prev,
        { id: `w-${m.ts}-${m.fromName}`, channel: "whisper", fromName: m.fromName, body: m.body, ts: m.ts },
      ]);
    });
    const onPartyChat = globalRoom.onMessage("party_chat", (m: { fromName: string; body: string; ts: number }) => {
      setMessages((prev) => [
        ...prev,
        { id: `p-${m.ts}-${m.fromName}`, channel: "party", fromName: m.fromName, body: m.body, ts: m.ts },
      ]);
    });
    const onChatError = globalRoom.onMessage("chat_error", (e: ChatErrorEvent) => {
      const text = CHAT_ERR_TEXT[e.reason] ?? `Chat error (${e.reason}).`;
      setError({ text, ...(e.autoMuteSeconds ? { until: Date.now() + e.autoMuteSeconds * 1000 } : {}) });
      if (e.reason === "muted" && e.until) setMuteUntilTs(new Date(e.until).getTime());
      if (e.autoMuteSeconds) setMuteUntilTs(Date.now() + e.autoMuteSeconds * 1000);
    });

    unsubs.push(() => onWhisper());
    unsubs.push(() => onPartyChat());
    unsubs.push(() => onChatError());

    return () => {
      for (const u of unsubs) u();
    };
  }, [globalRoom]);

  // Mute countdown ticker
  useEffect(() => {
    if (!muteUntilTs) return;
    const id = window.setInterval(() => forceTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [muteUntilTs]);

  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Enter focuses chat (when not already in an input)
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.code === "Enter") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isMuted = muteUntilTs !== null && muteUntilTs > Date.now();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || isMuted) return;
    if (channel === "whisper" && !whisperTo.trim()) {
      setError({ text: "Whisper needs a target character id." });
      return;
    }
    if (!globalRoom) return;
    const payload: { channel: ChatChannel; body: string; to?: string } = { channel, body };
    if (channel === "whisper") payload.to = whisperTo.trim();
    globalRoom.send("chat", payload);
    setDraft("");
  };

  const onLocalKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === "Escape") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const muteRemainingS = muteUntilTs ? Math.max(0, Math.ceil((muteUntilTs - Date.now()) / 1000)) : 0;

  return (
    <div style={wrapStyle} data-testid="game-chat">
      <div style={{ padding: "6px 8px", display: "flex", gap: 6, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        {(["global", "whisper", "party"] as ChatChannel[]).map((ch) => (
          <button
            key={ch}
            type="button"
            onClick={() => setChannel(ch)}
            style={{
              background: channel === ch ? channelColor(ch) : "transparent",
              color: channel === ch ? "#0b0d12" : channelColor(ch),
              border: `1px solid ${channelColor(ch)}55`,
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: 11,
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {ch}
          </button>
        ))}
        {channel === "whisper" && (
          <input
            type="text"
            placeholder="character id"
            value={whisperTo}
            onChange={(e) => setWhisperTo(e.target.value)}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#e6e8ee",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 11,
              fontFamily: "inherit",
            }}
          />
        )}
      </div>

      <div ref={logRef} style={{ padding: "6px 8px", height: 180, overflowY: "auto" }}>
        {messages.length === 0 ? (
          <div style={{ opacity: 0.55 }}>(no messages yet — press Enter to type)</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} style={{ marginBottom: 2 }}>
              <span style={{ color: channelColor(m.channel), fontSize: 10, marginRight: 4 }}>
                [{m.channel}]
              </span>
              <span style={{ color: channelColor(m.channel) }}>{m.fromName}</span>
              <span style={{ opacity: 0.6 }}>: </span>
              <span>{m.body}</span>
            </div>
          ))
        )}
      </div>

      {error && (
        <div style={{ padding: "4px 8px", color: "#ef4444", fontSize: 11, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          {error.text}
          {error.until && ` (auto-mute ${Math.ceil((error.until - Date.now()) / 1000)}s)`}
        </div>
      )}

      <form onSubmit={submit}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onLocalKey}
          placeholder={
            isMuted
              ? `Muted (${muteRemainingS}s remaining)`
              : !globalRoom
                ? "Connecting…"
                : "Press Enter to chat…"
          }
          disabled={isMuted || !globalRoom}
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#e6e8ee",
            fontSize: 12,
            padding: "6px 8px",
            width: "100%",
            fontFamily: "inherit",
            opacity: isMuted ? 0.4 : 1,
          }}
        />
      </form>
    </div>
  );
}
